/** @fileoverview Shared store of checklist and timeline rows, so blocks that show the same rows share one request and reload only when their rows change. */

import { createContext, useContext } from 'react';

import { stableStringify } from '../../lib/utils';

import type { ComponentStateApi } from '../../services/componentStateApi';
import type {
  ComponentStateRow,
  FabrikAction,
  FabrikComponent,
  FabrikRecordKeyRefValue,
  FabrikStateComponent,
  InputSnapshot,
} from '../../types/apps';
import type { FieldValues } from './types';

/** What the store knows about the rows of one component for one record. */
export type RowsEntry = {
  rows: ComponentStateRow[];
  /** True while a request for these rows is running. */
  loading: boolean;
  /** True once the first request has finished, with rows or with an error. */
  hasLoaded: boolean;
  error: string;
  /** The running request, so a second reader of the same rows waits for it instead of sending another. */
  promise: Promise<void> | null;
};

type RowsSlot = {
  componentId: string;
  fields: FieldValues;
  recordKey: FabrikRecordKeyRefValue[];
  screenId: string;
  inputs: InputSnapshot;
  entry: RowsEntry;
  listeners: Set<() => void>;
  /** Number of the latest request, so an older answer does not replace a newer one. */
  requestNumber: number;
};

export type ComponentStateStore = {
  getEntry: (key: string) => RowsEntry;
  subscribe: (request: {
    componentId: string;
    screenId: string;
    fields: FieldValues;
    inputs: InputSnapshot;
    recordKey: FabrikRecordKeyRefValue[];
    listener: () => void;
  }) => () => void;
  /** Reloads the given keys; the promise settles when every reload has finished. */
  invalidate: (keys: string[]) => Promise<void>;
};

const EMPTY_ROWS: ComponentStateRow[] = [];

export const EMPTY_ENTRY: RowsEntry = {
  rows: EMPTY_ROWS,
  loading: false,
  hasLoaded: false,
  error: '',
  promise: null,
};

/**
 * Key of the rows of one component for one record. The record key is part of it, so a change to
 * the globals the key reads loads the rows again.
 * @param componentId id of the checklist or timeline
 * @param recordKey refs that identify the component's record
 * @param fields record-key fields of the screen
 */
export const rowsKeyOf = (
  componentId: string,
  recordKey: FabrikRecordKeyRefValue[],
  fields: FieldValues,
  screenId = '',
  inputs: InputSnapshot = {}
) =>
  `${componentId}|${screenId}|${stableStringify(recordKey)}|${stableStringify(
    fields
  )}|${stableStringify(inputs)}`;

/**
 * Creates a store that loads rows through the given API and keeps them while anyone shows them.
 * Rows of a component are dropped when the last block that shows them goes away, so opening the
 * record again loads fresh rows. During a reload the old rows stay, so the screen does not flicker.
 * @param api the component state calls of the current mode
 */
export const createComponentStateStore = (api: ComponentStateApi): ComponentStateStore => {
  const slots = new Map<string, RowsSlot>();

  /**
   * Replaces the entry of one slot and tells its readers.
   * @param slot the slot to change
   * @param entry the new entry
   */
  const setEntry = (slot: RowsSlot, entry: RowsEntry) => {
    slot.entry = entry;
    slot.listeners.forEach((listener) => listener());
  };

  /**
   * Starts a request for the rows of one slot. A newer request replaces the answer of an older one.
   * Returns a promise that settles when the request has finished, or at once when nobody shows the rows.
   * @param key key of the slot
   */
  const load = (key: string): Promise<void> => {
    const slot = slots.get(key);
    if (!slot) {
      return Promise.resolve();
    }
    const { componentId, screenId, fields, inputs, recordKey } = slot;
    const requestNumber = slot.requestNumber + 1;
    slot.requestNumber = requestNumber;
    /** True when this request is still the latest one for a slot that is still in use. */
    const isLatest = () => slots.get(key) === slot && slot.requestNumber === requestNumber;
    const promise: Promise<void> = api
      .read({ componentId, screenId, fields, inputs, recordKey })
      .then((result) => {
        if (!isLatest()) {
          return;
        }
        const rows = Array.isArray(result?.rows) ? result.rows : EMPTY_ROWS;
        setEntry(slot, { rows, loading: false, hasLoaded: true, error: '', promise: null });
      })
      .catch((reason: unknown) => {
        if (!isLatest()) {
          return;
        }
        setEntry(slot, {
          rows: EMPTY_ROWS,
          loading: false,
          hasLoaded: true,
          error: reason instanceof Error ? reason.message : 'Unable to load this block.',
          promise: null,
        });
      });
    setEntry(slot, { ...slot.entry, loading: true, promise });
    return promise;
  };

  return {
    getEntry: (key) => slots.get(key)?.entry ?? EMPTY_ENTRY,
    subscribe: ({ componentId, screenId, fields, inputs, recordKey, listener }) => {
      const key = rowsKeyOf(componentId, recordKey, fields, screenId, inputs);
      let slot = slots.get(key);
      if (!slot) {
        slot = {
          componentId,
          fields,
          recordKey,
          screenId,
          inputs,
          entry: EMPTY_ENTRY,
          listeners: new Set(),
          requestNumber: 0,
        };
        slots.set(key, slot);
        slot.listeners.add(listener);
        load(key);
      } else {
        slot.listeners.add(listener);
      }
      const subscribedSlot = slot;
      return () => {
        subscribedSlot.listeners.delete(listener);
        if (subscribedSlot.listeners.size === 0 && slots.get(key) === subscribedSlot) {
          slots.delete(key);
        }
      };
    },
    invalidate: async (keys) => {
      await Promise.all(keys.map((key) => load(key)));
    },
  };
};

type ComponentStateStoreValue = {
  /** Null when rows cannot be read, for example in the builder before the app is saved. */
  store: ComponentStateStore | null;
  actions: FabrikAction[];
  /** Ids of the actions the viewer is in no allowed group for; their buttons are hidden. */
  blockedActionIds: ReadonlySet<string>;
};

/*
 * Kept apart from AppRuntimeContext, so blocks that only read `mode` or list data do not
 * render again when rows change.
 */
const ComponentStateStoreContext = createContext<ComponentStateStoreValue>({
  store: null,
  actions: [],
  blockedActionIds: new Set(),
});

export const ComponentStateStoreProvider = ComponentStateStoreContext.Provider;

/** Returns the shared rows store and the actions of the definition. */
export const useComponentStateStore = () => useContext(ComponentStateStoreContext);

/**
 * True when the viewer is in none of the action's allowed groups, so its button stays hidden.
 * @param actionId id of the action
 */
export const useIsActionBlocked = (actionId: string) =>
  useContext(ComponentStateStoreContext).blockedActionIds.has(actionId);

/**
 * Ids of the components whose rows an action writes.
 * @param actions actions of the definition
 * @param actionId id of the action
 */
export const getActionTargetIds = (actions: FabrikAction[], actionId: string): string[] => {
  const action = actions.find((candidate) => candidate.id === actionId);
  if (!action) {
    return [];
  }
  const targetIds = action.steps.flatMap((step) =>
    step.kind === 'store' ? [step.target.componentId] : []
  );
  return [...new Set(targetIds)];
};

/** Record-key refs of checklist components whose rows an action's send steps read. */
export const getActionSendRecordKeyRefs = (
  actions: FabrikAction[],
  actionId: string,
  components: FabrikComponent[]
): FabrikRecordKeyRefValue[] => {
  const action = actions.find((candidate) => candidate.id === actionId);
  if (!action) {
    return [];
  }
  const componentIds = new Set(
    action.steps.flatMap((step) =>
      step.kind === 'send' && step.items ? [step.items.componentId] : []
    )
  );
  const leaves: FabrikComponent[] = [];
  const visit = (component: FabrikComponent) => {
    if (component.kind === 'grid') component.items.forEach(({ component: child }) => visit(child));
    else if (component.kind === 'card') visit(component.content);
    else leaves.push(component);
  };
  components.forEach(visit);
  return [...componentIds].flatMap((componentId) => {
    const component = leaves.find(
      (candidate): candidate is FabrikStateComponent =>
        candidate.id === componentId &&
        (candidate.kind === 'checklist' || candidate.kind === 'timeline')
    );
    return component?.recordKey ?? [];
  });
};
